"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, FileCheck, MessageCircle, ShieldCheck } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import {
  tenderFaqs,
  tenderOemBuilds,
  tenderPriceNote,
  tenderScope,
  tenderSpecRows,
  tenderSupport,
} from "@/content/enterprise/dgx-spark-tender";
import { dgxSparkMarketPrice } from "@/content/enterprise/dgx-spark";
import { whatsappHref } from "@/lib/whatsapp";

// RFQ / tender support for the DGX Spark family. Substance and the accuracy gates
// live in content/enterprise/dgx-spark-tender.ts — read that header before editing
// any claim here, particularly the MOF and LOA wording.

export default function DgxSparkTender() {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);

  return (
    <main className="bg-background">
      {/* Hook — the reader has a document to write and a closing date */}
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Enterprise Solutions")} · {t(locale, "Tender & RFQ Support")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl">
              {t(locale, "DGX Spark tender support — we know what your RFQ needs")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
              {t(
                locale,
                "You are writing a specification, you need a budget figure your approver will accept, and the bid cannot fall over on authorisation. We supply every DGX Spark build, obtain the OEM Letter of Authorisation for your tender, and return a formal quotation in one working day.",
              )}
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                { icon: FileCheck, label: "Spec written to be lifted into your document" },
                { icon: ShieldCheck, label: "OEM authorisation across all eight builds" },
                { icon: Clock, label: "Quotation in one working day" },
              ].map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card p-4"
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                  <span className="text-sm leading-relaxed text-neutral-emphasis">
                    {t(locale, label)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                href={lp("/enterprise/dgx-spark-malaysia/enquiry/")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Send your RFQ")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <a
                href="#tender-spec"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "Copy the specification")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What we do, in the order a tender team needs it */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "How we support your RFQ")}
          </motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {tenderSupport.map((s) => (
              <motion.div {...inView} key={s.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, s.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, s.detail)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The copy-paste block */}
      <section id="tender-spec" className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Baseline specification for your tender document")}
          </motion.h2>
          <motion.p {...inView} className="mt-3 max-w-3xl text-neutral-muted">
            {t(
              locale,
              "Phrased as minimum requirements rather than product copy, so it can go into the document as it stands. Every parameter here is fixed by NVIDIA across all eight OEM builds — mandate these and the tender still admits eight compliant bidders.",
            )}
          </motion.p>
          <motion.div {...inView} className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-3 pr-6 font-semibold text-neutral-emphasis">
                    {t(locale, "Requirement")}
                  </th>
                  <th className="py-3 font-semibold text-neutral-emphasis">
                    {t(locale, "Baseline")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenderSpecRows.map((r) => (
                  <tr key={r.requirement} className="border-b border-border/60 align-top">
                    <td className="py-3 pr-6 font-medium text-neutral-emphasis">
                      {t(locale, r.requirement)}
                    </td>
                    <td className="py-3 leading-relaxed text-neutral-muted">{t(locale, r.baseline)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Equivalence — the differentiator */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Every compliant build — so the tender is not locked to one brand")}
          </motion.h2>
          <motion.p {...inView} className="mt-3 max-w-3xl text-neutral-muted">
            {t(
              locale,
              "Public procurement cannot lawfully specify a single manufacturer. The compute here genuinely does not vary between badges, which makes an 'or equivalent' clause unusually clean to write. We supply all eight and can obtain authorisation for any of them.",
            )}
          </motion.p>
          <motion.div {...inView} className="mt-8 grid gap-4 sm:grid-cols-2">
            {tenderOemBuilds.map((o) => (
              <div key={`${o.brand}-${o.model}`} className="rounded-lg border border-border bg-card p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                  {o.brand}
                </p>
                <p className="mt-0.5 font-semibold text-neutral-emphasis">{o.model}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, o.note)}</p>
                {o.page && (
                  <Link
                    href={lp(`/enterprise/${o.page}/`)}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    {t(locale, "Full specification")}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                )}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Budget figure */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="rounded-lg border border-border bg-card p-8">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "Indicative budget, before a tender exists")}
            </h2>
            <p className="mt-4 font-display text-3xl font-bold text-primary">
              {dgxSparkMarketPrice.low} – {dgxSparkMarketPrice.high}
              <span className="ml-2 align-middle text-sm font-normal text-neutral-muted">
                {t(locale, "per unit, public retail reference")}
              </span>
            </p>
            <p className="mt-4 leading-relaxed text-neutral-muted">{t(locale, tenderPriceNote)}</p>
          </motion.div>
        </div>
      </section>

      {/* Honest scope — no MOF claim */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-xl font-bold text-neutral-emphasis md:text-2xl">
            {t(locale, "Where we sit in your procurement process")}
          </motion.h2>
          <motion.p {...inView} className="mt-4 leading-relaxed text-neutral-muted">
            {t(locale, tenderScope)}
          </motion.p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <StructuredData type="faq" faqs={tenderFaqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Tender and RFQ questions")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {tenderFaqs.map((f) => (
              <motion.div {...inView} key={f.question} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis">
            {t(locale, "Send the tender reference and closing date")}
          </motion.h2>
          <motion.p {...inView} className="mx-auto mt-4 max-w-2xl text-neutral-muted">
            {t(
              locale,
              "Configuration, quantity and closing date is enough to start. Quotation back within one working day, and the authorisation request runs alongside it rather than after.",
            )}
          </motion.p>
          <motion.div {...inView} className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={lp("/enterprise/dgx-spark-malaysia/enquiry/")}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Send your RFQ")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a
              href={whatsappHref("Hi Anchor Sprint, I have a DGX Spark tender with a tight deadline (from your DGX Spark tender page).")}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="dgx_spark_tender_cta"
              data-track-phone="601159246128"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t(locale, "Tight deadline? WhatsApp us")}
            </a>
          </motion.div>
          <motion.p {...inView} className="mt-8 text-sm text-neutral-muted">
            <Link href={lp("/enterprise/dgx-spark-malaysia/")} className="text-primary hover:underline">
              {t(locale, "DGX Spark in Malaysia — every OEM build")}
            </Link>
            {" · "}
            <Link href={lp("/enterprise/nvidia-malaysia/")} className="text-primary hover:underline">
              {t(locale, "NVIDIA in Malaysia")}
            </Link>
          </motion.p>
        </div>
      </section>
    </main>
  );
}
